import type { Ref } from "react";
import { Button, Icons } from "@/components/ui";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { wallMessages } from "@/i18n/messages/wall";
import { isMacPlatform } from "@/lib/os-platform";
import "./wall-surface.css";

interface WallSearchFieldProps {
  query: string;
  onQueryChange: (query: string) => void;
  /** ⌘K puts the cursor here. */
  ref?: Ref<HTMLInputElement>;
  placeholder: string;
  /** Unread notifications: a red dot while any, else a bell. */
  unread: number;
  notificationsOpen: boolean;
  onToggleNotifications: () => void;
  /** The dot: focus returns here when the notification list closes. */
  notificationsButtonRef?: Ref<HTMLButtonElement>;
}

const SHORTCUT = isMacPlatform() ? "⌘K" : "Ctrl K";

/**
 * The wall's search (mockup `.search`): a 40px pill field with the
 * magnifier, the ⌘K (Ctrl K) hint and the notification dot, in the wall
 * chrome; focus rings it in the accent. Escape closes an open notification
 * list and keeps the query; otherwise it clears a query, and on an empty
 * field leaves it.
 */
export function WallSearchField({
  query,
  onQueryChange,
  ref,
  placeholder,
  unread,
  notificationsOpen,
  onToggleNotifications,
  notificationsButtonRef,
}: WallSearchFieldProps) {
  const t = useT(wallMessages);
  const tHeader = useT(panelsHeaderMessages);
  return (
    <label
      className="flex h-10 min-w-0 flex-1 cursor-text items-center gap-2.5 rounded-pill pl-3.5 pr-2 text-secondary wall-chrome focus-within:shadow-[0_0_0_2px_color-mix(in_srgb,var(--color-accent)_45%,transparent)]"
      data-testid="wall-search"
    >
      <Icons.Search className="h-4 w-4 shrink-0" aria-hidden="true" />
      <input
        ref={ref}
        type="text"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={(event) => {
          // The open list closes itself on this Escape.
          if (event.key !== "Escape" || notificationsOpen) return;
          event.preventDefault();
          if (query) onQueryChange("");
          else event.currentTarget.blur();
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="min-w-0 flex-1 bg-transparent text-appkit-subhead text-primary caret-accent outline-none placeholder:text-muted"
        data-testid="wall-search-field"
      />
      <kbd
        aria-hidden="true"
        className="mr-1 shrink-0 font-sans text-appkit-caption text-muted max-[640px]:hidden"
        data-testid="wall-search-shortcut"
      >
        {SHORTCUT}
      </kbd>
      <Button
        ref={notificationsButtonRef}
        type="button"
        variant="ghost"
        size="iconSm"
        className="shrink-0 text-secondary hover:bg-[var(--color-interactive-hover)] focus-visible:ring-2 focus-visible:ring-accent aria-expanded:bg-[var(--color-interactive-hover)] aria-expanded:text-primary"
        aria-label={
          unread > 0
            ? t("unreadNotifications", { count: unread })
            : tHeader("notifications")
        }
        aria-expanded={notificationsOpen}
        aria-haspopup="dialog"
        title={tHeader("notifications")}
        onClick={(event) => {
          // A click inside the label would otherwise focus the field.
          event.preventDefault();
          onToggleNotifications();
        }}
        data-testid="wall-search-unread"
      >
        {unread > 0 ? (
          <span
            className="h-[7px] w-[7px] rounded-full bg-danger"
            data-testid="wall-search-unread-dot"
          />
        ) : (
          <Icons.Bell className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </Button>
    </label>
  );
}
